import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { staffAPI } from '../api';
import { useAuth } from './AuthContext';
import { useStore } from './StoreContext';
import { TIER_RANK } from '../config/navigation';

const PermissionContext = createContext(null);

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

export const PermissionProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const { selectedStore } = useStore();
  const [permissions, setPermissions] = useState([]);
  const [storeRole, setStoreRole] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchPermissions = useCallback(async (storeId) => {
    const maxRetries = 2;
    const delays = [1000, 2000];

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        setLoading(true);
        const response = await staffAPI.getMyPermissions(storeId);
        setPermissions(response.data.permissions || []);
        setStoreRole(response.data.role || null);
        return;
      } catch (err) {
        const status = err?.response?.status;
        if (status >= 500 && attempt < maxRetries) {
          await new Promise(r => setTimeout(r, delays[attempt]));
          continue;
        }
        // Owners/admins not in staff table — grant full access
        if (user?.role === 'admin' || user?.role === 'owner') {
          setStoreRole(user.role);
          setPermissions([]);
        } else {
          setPermissions([]);
          setStoreRole(null);
        }
        return;
      } finally {
        setLoading(false);
      }
    }
  }, [user]);

  useEffect(() => {
    if (isAuthenticated && selectedStore?.id) {
      fetchPermissions(selectedStore.id);
    } else {
      setPermissions([]);
      setStoreRole(null);
    }
  }, [isAuthenticated, selectedStore, fetchPermissions]);

  const isOwnerOrAdmin = storeRole === 'admin' || storeRole === 'owner';

  const hasPermission = useCallback((permission) => {
    if (isOwnerOrAdmin) return true;
    return permissions.includes(permission);
  }, [permissions, isOwnerOrAdmin]);

  const hasAnyPermission = useCallback((permissionList) => {
    if (isOwnerOrAdmin) return true;
    return permissionList.some(p => permissions.includes(p));
  }, [permissions, isOwnerOrAdmin]);

  /**
   * Check if the current store's subscription meets a minimum tier.
   * Usage: hasTier('enterprise') → true only if tier is enterprise
   *        hasTier('ai_pro')    → true if tier is ai_pro OR enterprise
   */
  const hasTier = useCallback((requiredTier) => {
    const storeTier = selectedStore?.subscription?.tier || 'free';
    const currentRank = TIER_RANK[storeTier] ?? 0;
    const requiredRank = TIER_RANK[requiredTier] ?? 99;
    return currentRank >= requiredRank;
  }, [selectedStore]);

  const value = {
    permissions,
    storeRole,
    loading,
    hasPermission,
    hasAnyPermission,
    hasTier,          // NEW — check subscription tier
    isOwnerOrAdmin,
    refetchPermissions: () => selectedStore?.id && fetchPermissions(selectedStore.id),
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};
